<template>
  <div v-if="config">
    <div class='bg-gray-200 p-3 rounded' v-if="!class_configuration_id && level < 1">
      <p>Select a saved template to use as a starting point</p>
      <copy-config-template @configChange="setConfigTemplate"/>
      <import-config-from-c-s-v @configChange="setConfigTemplate"/>
    </div>
    <div class="flex justify-end">
      <dropdown-button ref="DivisionDropdown" class="mb-2">
        <span slot="link" class="inline-flex items-center rounded-md border border-transparent px-2 py-1 text-sm font-medium leading-4 text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
            <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM12.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM18.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
          </svg>
          Settings
        </span>
        <div slot="dropdown" class="w-64 bg-white border shadow rounded-md overflow-hidden divide-y divide-gray-200 text-sm">
          <div class="py-1">
            <div class="px-2 py-2 text-gray-800 hover:bg-gray-200">
              <div class="relative flex items-start">
                <div class="flex h-5 items-center">
                  <input v-model="config.advanced_name_settings" id="comments" aria-describedby="comments-description" name="comments" type="checkbox" class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                </div>
                <div class="ml-3 text-sm">
                  <label for="comments" class="font-medium text-gray-700">Advanced Name Settings</label>
                  <p id="comments-description" class="text-gray-600">Use advanced settings for determining class names.</p>
                </div>
              </div>
            </div>

            <div v-if="!config.advanced_config" class="py-1" role="none">
              <button type="button" @click="enableAdvancedConfig" class="text-gray-900 group flex w-full items-center px-2 py-2 text-gray-800 hover:bg-gray-200">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="mr-3 w-6 h-6 text-green-600">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Enable Advanced Config
              </button>
            </div>
            <div v-else class="py-1" role="none">
              <button type='button' @click="removeAdvancedConfig" class="text-gray-900 group flex w-full items-center px-2 py-2 text-gray-800 hover:bg-gray-200">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="mr-3 w-6 h-6 text-red-600">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Remove Advanced Config
              </button>
            </div>
            <div v-if="!config.divisions" class="py-1" role="none">
              <button type="button" @click="enableDivisions" class="text-gray-900 group flex w-full items-center px-2 py-2 text-gray-800 hover:bg-gray-200">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="mr-3 w-6 h-6 text-green-600">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Enable Divisions
              </button>
            </div>
            <div v-else class="py-1" role="none">
              <button type='button' @click="removeDivisions" class="text-gray-900 group flex w-full items-center px-2 py-2 text-gray-800 hover:bg-gray-200">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="mr-3 w-6 h-6 text-red-600">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Remove Divisions
              </button>
            </div>
            <div v-if="!config.classes" class="py-1" role="none">
              <button type="button" @click="enableClasses" class="text-gray-900 group flex w-full items-center px-2 py-2 text-gray-800 hover:bg-gray-200">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="mr-3 w-6 h-6 text-green-600">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v6m3-3H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Enable Classes
              </button>
            </div>
            <div v-else class="py-1" role="none">
              <button type='button' @click="removeClasses" class="text-gray-900 group flex w-full items-center px-2 py-2 text-gray-800 hover:bg-gray-200">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="mr-3 w-6 h-6 text-red-600">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Remove Classes
              </button>
            </div>
          </div>
        </div>
      </dropdown-button>
    </div>
    <expandable title="Basic Settings" class="bg-gray-200">
      <div slot="title" class='flex items-end flex-1 p-3 w-full rounded space-x-1'>
        <p class='font-bold'>Basic Settings - </p>
        <p class="">{{config.name}} classes by {{config.break_type}}</p>
      </div>
      <div slot="expandable" class="bg-white p-4">
        <div class="grid grid-rows- grid-flow-row grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label class="form-label">Base Name/Label </label>
            <input class="new-form-input mt-1 w-full" v-model="config.name" for="label/name" placeholder="Class List Label">
            <div class="relative flex items-start">
              <div class="flex h-5 items-center">
                <input v-model="config.no_name_in_class" id="comments" aria-describedby="comments-description" name="comments" type="checkbox" class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
              </div>
              <div class="ml-3 text-sm">
                <label for="comments" class="font-medium text-gray-700">Don't Include in Class Name</label>
              </div>
            </div>
          </div>
          <div>
            <label class="form-label">Break By</label>
            <select v-model="config.break_type" id="location" name="location" class="mt-1 block w-full rounded-md border border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm">
              <option :value="null" selected disabled>
                Please select the break type
              </option>
              <option value='weight'>Weight</option>
              <option value='age'>Age</option>
              <option value="none">None</option>
            </select>
            <p class="mt-1 text-sm text-gray-700" id="question-description">How will you break clases?</p>
          </div>
          <div v-if="level > 0">
            <label class="form-label">Division Label</label>
            <input class="new-form-input mt-1 w-full" type="text" v-model="config.division_label">
            <p class="mt-1 text-sm text-gray-700" id="question-description">How the division will be labeled in the dropdown for new entries. Default to just be the name.</p>
          </div>
        </div>
      </div>
    </expandable>
    <expandable title="Champion Settings" class="bg-gray-200">
      <div slot="title" class='flex items-center flex-1 p-3  w-full rounded space-x-1'>
        <p class="font-bold">Champion Settings - </p>
        <p>
          Top {{config.champions}} {{config.champion_title}}
          and top {{config.division_champions}} in division
        </p>
      </div>
      <div slot="expandable" class="bg-white p-4">
        <div class="grid grid-rows- grid-flow-row grid-cols-1 sm:grid-cols-3 gap-4">
          <div id="champions">
            <div>
              <label class="form-label">Champions Selected</label>
              <input class="new-form-input mt-1 w-full" type="number" v-model.number="config.champions">
              <p class="mt-1 text-sm text-gray-700" id="question-description">How many champions will you be selecting?</p>
            </div>
          </div>
          <div>
            <label class="form-label">Champion Title</label>
            <select v-model="config.champion_title" id="location" name="location" class="mt-1 block w-full rounded-md border border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm">
              <option :value="null" selected disabled>
                Please select the break type
              </option>
              <option value="Champion">Champion (Basic)</option>
              <option value='Overall'>Overall</option>
              <option value='Grand'>Grand</option>
              <option value='Supreme'>Supreme</option>
              <option value='Best In Show'>Best In Show</option>
            </select>
            <p class="mt-1 text-sm text-gray-700" id="question-description">How do you want to refer to this champion?</p>
          </div>
          <div id="division_champions" v-if="!config.divisions" >
            <div>
              <label class="form-label">Division Champions Selected</label>
              <input class="new-form-input mt-1 w-full" type="number" v-model.number="config.division_champions">
              <p class="mt-1 text-sm text-gray-700" id="question-description">How many division champions will you be selecting?</p>
            </div>
          </div>
          <div v-else>
            <div>
              <label class="form-label">Division Champions can be updated in the "Divisions" Section</label>
              <input class="new-form-input-disabled mt-1 w-full " disabled type="number" v-model.number="config.division_champions">
            </div>
          </div>
        </div>
      </div>
    </expandable>
    <expandable v-if="level === 0 || (!config.classes && computedBaseConfig.class_calc !== 'none')" title="Age Settings" class="bg-gray-200">
      <div slot="title" class='flex items-center flex-1 p-3 space-x-1 w-full rounded'>
        <p class="font-bold">Age Settings - </p>
        <p> {{config.class_calc ? config.class_calc : 'none'}}</p>
      </div>
      <div slot="expandable" class="bg-white p-4">
        <div v-if="level === 0" class="grid grid-rows- grid-flow-row grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label for="new_division" class="form-label">Calc Class By</label>
            <select v-model="config.class_calc" id="location" name="location" class="mt-1 block w-full rounded-md border border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm">
              <option :value="null" selected disabled>
                Please select calc type
              </option>
              <option value='date_range'>Date Range</option>
              <option value='age'>Specific Age</option>
              <option value='none'>None</option>
            </select>
          </div>
          <template v-if="config.class_calc === 'age'">
            <div id="base-date">
              <label for="new_division" class="form label">Base Date</label>
              <input v-if="!config.base_date_day_of_show" class="new-form-input mt-1 w-full" v-model="config.base_date" type="date">
              <div class="relative flex items-start mt-1">
                <div class="flex h-5 items-center">
                  <input v-model="config.base_date_day_of_show" id="comments" aria-describedby="comments-description" name="comments" type="checkbox" class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                </div>
                <div class="ml-3 text-sm">
                  <label for="comments" class="font-medium text-gray-700">Use Day of Show</label>
                  <p id="comments-description" class="text-gray-600">Override base date and use day of show.</p>
                </div>
              </div>
            </div>
            <div id="age-unit">
              <label for="new_division" class="form-label">Age Unit</label>
              <select v-model="config.age_unit" id="location" name="location" class="mt-1 block w-full rounded-md border border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm">
                <option :value="null" selected disabled>
                  Please select age unit
                </option>
                <option value='YEARS'>Years</option>
                <option value='MONTHS'>Months</option>
              </select>
              <div class="relative flex items-start mt-1">
                <div class="flex h-5 items-center">
                  <input v-model="config.age_gap" id="comments" aria-describedby="comments-description" name="comments" type="checkbox" class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                </div>
                <div class="ml-3 text-sm">
                  <label for="comments" class="font-medium text-gray-700">Use Age Gap?</label>
                  <p id="comments-description" class="text-gray-600">Do your ages leave a gap? (0-8, 9-12, 13-18)</p>
                </div>
              </div>
            </div>
          </template>
        </div>
        <div v-if="(!config.classes && computedBaseConfig.class_calc !== 'none') && level !==0" class="grid grid-rows- grid-flow-row grid-cols-1 sm:grid-cols-3 gap-4">
          <template v-if="computedBaseConfig.class_calc === 'date_range'">
            <div>
              <label class="form-label">Start Date</label>
              <input v-if="computedBaseConfig.class_calc === 'date_range'" class="new-form-input mt-1 text-sm" v-model="config.start_date" type="date">
            </div>
            <div>
              <label class="form-label">End Date</label>
              <input v-if="computedBaseConfig.class_calc === 'date_range'" class="new-form-input mt-1 text-sm" v-model="config.end_date" type="date">
            </div>
          </template>
          <template v-else-if="baseConfig.class_calc === 'age'">
            <div>
              <label class="form-label">Age Start</label>
              <input class="new-form-input mt-1 text-sm" v-model="config.start_date" type="number">
            </div>
            <div>
              <label class="form-label">Age End</label>
              <input class="new-form-input mt-1 text-sm" v-model="config.end_date" type="number">
            </div>
          </template>
        </div>
      </div>
    </expandable>
    <expandable v-if="level < 3 && config.divisions"  title="Divisions" class="bg-gray-200">
      <div slot="title" class='flex items-center flex-1 p-3 space-x-1 w-full rounded'>
        <p class="font-bold">Divisions </p>
        <p>({{config.divisions.length}})</p>
      </div>
      <div slot="expandable" class="bg-white p-4">
          <div class="border-b border-gray-400 pb-3 mb-3 sm:flex sm:items-end justify-end">
            <div class="flex items-center space-x-2" v-if="config.divisions">
              <div class="flex-shrink-0">
                <div class="mt-1 flex rounded-md">
                  <div class="relative flex flex-grow items-stretch focus-within:z-10">
                    <input v-model="newDivision" v-on:keyup.enter="addDivision()" type="email" name="divisino_name" id="email" class="block w-full rounded-none rounded-l-md border border-gray-300 px-2 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="Division Name">
                  </div>
                  <button type="button" @click="addDivision()" class="relative -ml-px inline-flex items-center space-x-2 rounded-r-md border border-gray-300 bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                    <span>Add</span>
                  </button>
                </div>
              </div>
              <dropdown-button ref="DivisionDropdown">
                <span slot="link" class="inline-flex items-center rounded-md border border-transparent px-2 py-1 text-sm font-medium leading-4 text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM12.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM18.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
                  </svg>
                </span>
                <div slot="dropdown" class="w-64 bg-white border shadow rounded-md overflow-hidden divide-y divide-gray-200 text-sm">
                  <div class="py-1" role="none">
                    <div class="px-2 py-2 text-gray-800 hover:bg-gray-200">
                      <label for="new_division" class="block text-sm font-medium text-gray-700">Set Division Champions</label>
                      <div class="mt-1 flex rounded-md">
                        <div class="relative flex flex-grow items-stretch focus-within:z-10">
                          <input v-model="config.division_champions" v-on:keyup.enter="updateDivisionChampions()" type="number" name="divisino_name" id="email" class="block w-full rounded-none rounded-l-md border border-gray-300 px-2 focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" placeholder="Division Name">
                        </div>
                        <button type="button" @click="updateDivisionChampions()" class="relative -ml-px inline-flex items-center space-x-2 rounded-r-md border border-gray-300 bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                          <span>Update</span>
                        </button>
                      </div>
                    </div>
                    <div class="px-2 py-2 text-gray-800 hover:bg-gray-200">
                      <label for="new_division" class="block text-sm font-medium text-gray-700">Set Break Type</label>
                      <div class="mt-1 flex rounded-md">
                        <div class="relative flex flex-grow items-stretch focus-within:z-10">
                          <select v-model="config.break_type" id="location" name="location" class="block w-full rounded-l-md border border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm">
                            <option :value="null" selected disabled>
                              Please select the break type
                            </option>
                            <option value='weight'>Weight</option>
                            <option value='age'>Age</option>
                            <option value="none">None</option>
                          </select>
                        </div>
                        <button type="button" @click="updateBreakBy()" class="relative -ml-px inline-flex items-center space-x-2 rounded-r-md border border-gray-300 bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500">
                          <span>Update</span>
                        </button>
                      </div>
                    </div>
                    <div class="px-2 py-2 text-gray-800 hover:bg-gray-200">
                      <div class="relative flex items-start">
                        <div class="flex h-5 items-center">
                          <input v-model="config.alternate_sub_division_by_division" id="comments" aria-describedby="comments-description" name="comments" type="checkbox" class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                        </div>
                        <div class="ml-3 text-sm">
                          <label for="comments" class="font-medium text-gray-700">Alternate Between Divisions</label>
                          <p id="comments-description" class="text-gray-600">Go from one subdivision within each division to the next.</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </dropdown-button>
            </div>
          </div>
          <div v-if="config.divisions">
            <draggable class="mt-2"  v-model="config.divisions" @start="drag=true" @end="drag=false" handle=".handle">
              <div v-for="(d, i) in config.divisions" :key="i" class="flex mb-2 justify-between items-center pb-2 border-b">
                <div v-if="d.edit" class="w-full">
                  <div class="flex justify-between bg-gray-300 p-2 rounded items-center">
                    <p class="">{{d.name}}</p>
                    <button class='btn btn-xs btn-blue' @click="doneEditingDivision(d)">Done Editing</button>
                  </div>
                  <division-config class="ml-4 mt-2 pl-4 border-l" :config="divisionConfig" :baseConfig="baseConfig.name ? baseConfig : config" :level="level +1"/>
                </div>
                <template v-else>
                  <div class="flex items-center w-full">
                    <div v-if="!divisionConfig" class="text-xl handle draggable mr-3">&#9776;</div>
                    <div class="w-full">
                      <div class="flex justify-between">
                        <p class="">
                          {{d.name}}
                          <span class='text-xs font-bold' v-if="d.division_label">(Label: {{d.division_label}})</span>
                        </p>
                      </div>
                      <div class="flex justify-start text-xs font-medium space-x-2">
                        <span>Break By: {{d.break_type ? d.break_type : config.break_type}}</span>
                        <span>Champions: {{d.champions !== null ? d.champions : config.division_champions}}</span>
                        <span v-if="!d.divisions">Sub Division Champions: {{d.division_champions !== null ? d.division_champions : config.division_champions}}</span>
                      </div>
                      <div class="flex justify-start space-x-5">
                        <div v-if="d.divisions" class="text-xs font-medium">
                          Sub divisions:
                          <p v-for="(sd, j) in d.divisions" :key="j">{{sd.name}} - {{sd.champions}} Champions</p>
                        </div>
                        <div v-if="d.classes" class="text-xs font-medium">
                          Classes:
                          <p v-for="(c, j) in d.classes" :key="j">{{c.class_name}}</p>
                        </div>
                        <div v-else-if="hasAgeSettings && !config.classes" class="text-xs font-medium">
                          Age Start: {{d.start_date}} &nbsp; Age End: {{d.end_date}}
                        </div>
                      </div>
                    </div>
                  </div>
                  <div class="flex-shrink-0">
                    <button class='btn btn-xs btn-blue' @click="editDivisionClicked(d)">Edit</button>
                    <button @click="config.divisions.splice(i,1)" class='ml-2 btn btn-xs btn-red-outline'>x</button>
                  </div>
                </template>
              </div>
            </draggable>
          </div>
      </div>
    </expandable>
    <expandable v-if="level < 3 && config.classes" title="Classes" class="bg-gray-200">
      <div slot="title" class='flex items-center flex-1 p-3 space-x-1 w-full rounded'>
        <p class="font-bold">Classes</p>
        <p>({{config.classes.length}})</p>
      </div>
      <div slot="expandable" class="bg-white p-2">
        <div class="border-b border-gray-400 pb-3 mb-3 sm:flex sm:items-end sm:justify-end">
          <div class="flex items-center space-x-2" v-if="config.classes">
            <button @click="addClass">Add</button>
            <dropdown-button ref="DivisionDropdown">
              <span slot="link" class="inline-flex items-center rounded-md border border-transparent px-2 py-1 text-sm font-medium leading-4 text-gray-700 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-6 h-6">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M6.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM12.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0zM18.75 12a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
                </svg>
              </span>
              <div slot="dropdown" class="w-64 bg-white border shadow rounded-md overflow-hidden divide-y divide-gray-200 text-sm">
                <div class="py-1" role="none">
                  <div class="px-2 py-2 text-gray-800 hover:bg-gray-200">
                    <div class="relative flex items-start">
                      <div class="flex h-5 items-center">
                        <input v-model="config.class_sub_divisions" id="comments" aria-describedby="comments-description" name="comments" type="checkbox" class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                      </div>
                      <div class="ml-3 text-sm">
                        <label for="comments" class="font-medium text-gray-700">Has Sub Divisions</label>
                        <p id="comments-description" class="text-gray-600">Will you have sub_division for your classes?</p>
                      </div>
                    </div>
                  </div>
                  <div class="px-2 py-2 text-gray-800 hover:bg-gray-200">
                    <div class="relative flex items-start">
                      <div class="flex h-5 items-center">
                        <input v-model="config.alternate_classes_by_division" id="comments" aria-describedby="comments-description" name="comments" type="checkbox" class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                      </div>
                      <div class="ml-3 text-sm">
                        <label for="comments" class="font-medium text-gray-700">Alternate By Divisions</label>
                        <p id="comments-description" class="text-gray-600">Will the class for each division beore moving ot the next class.</p>
                      </div>
                    </div>
                  </div>
                </div>
                <!--<div class="py-1" role="none">
                  <div class="px-2 py-2 text-gray-800 hover:bg-gray-200">
                    <label for="new_division" class="block text-sm font-medium text-gray-700">Calc Class By</label>
                    <select v-model="config.class_calc" id="location" name="location" class="mt-1 block w-full rounded-md border border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm">
                      <option :value="null" selected disabled>
                        Please select calc type
                      </option>
                      <option value='date_range'>Date Range</option>
                      <option value='age'>Specific Age</option>
                      <option value='none'>None</option>
                    </select>
                  </div>
                  <template v-if="config.class_calc === 'age'">
                    <div class="px-2 py-2 text-gray-800 hover:bg-gray-200">
                      <label for="new_division" class="block text-sm font-medium text-gray-700">Base Date</label>
                      <input v-if="!config.base_date_day_of_show" class="new-form-input mt-1 w-full" v-model="config.base_date" type="date">
                      <div class="relative flex items-start mt-2 border-t pt-2">
                        <div class="flex h-5 items-center">
                          <input v-model="config.base_date_day_of_show" id="comments" aria-describedby="comments-description" name="comments" type="checkbox" class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                        </div>
                        <div class="ml-3 text-sm">
                          <label for="comments" class="font-medium text-gray-700">Use Day of Show</label>
                          <p id="comments-description" class="text-gray-600">Class the entry shows in will be calculated based on the day of the show.</p>
                        </div>
                      </div>
                      <div class="relative flex items-start mt-2 border-t pt-2">
                        <div class="flex h-5 items-center">
                          <input v-model="config.age_gap" id="comments" aria-describedby="comments-description" name="comments" type="checkbox" class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                        </div>
                        <div class="ml-3 text-sm">
                          <label for="comments" class="font-medium text-gray-700">Use Age Gap?</label>
                          <p id="comments-description" class="text-gray-600">Do your ages leave a gap? i.e. 0-8, 9-12, 13-18</p>
                        </div>
                      </div>
                    </div>
                    <div class="px-2 py-2 text-gray-800 hover:bg-gray-200">
                      <label for="new_division" class="block text-sm font-medium text-gray-700">Age Unit</label>
                      <select v-model="config.age_unit" id="location" name="location" class="mt-1 block w-full rounded-md border border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm">
                        <option :value="null" selected disabled>
                          Please select age unit
                        </option>
                        <option value='YEARS'>Years</option>
                        <option value='MONTHS'>Months</option>
                      </select>
                    </div>
                  </template>
                </div>-->
              </div>
            </dropdown-button>
          </div>
        </div>
        <!--<div class="border-b border-gray-400 pb-3 mb-3 sm:flex sm:items-end sm:justify-between">
          <h3 class="text-lg font-medium leading-6 text-gray-900">Classes</h3>
          <div class="mt-3 sm:mt-0 sm:ml-4">
            <button @click="enableClasses" class="inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-3 py-2 text-sm font-medium leading-4 text-white shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2">
              <span v-if="!config.classes">Enable Classes</span>
              <span v-else>Remove All Classes</span>
            </button>
          </div>
        </div>-->
        <div v-if="config.classes">
          <div class="flex justify-between border-b p-1 mb-1">
            <div class="w-8"></div>
            <p class="w-full text-center">Name</p>
            <div class="flex space-x-3 text-center">
              <p v-if="config.class_sub_divisions" class="w-32 text-center">Sub Division</p>
              <div class="w-8" v-if="computedBaseConfig.break_type === 'age'">By Weight</div>
              <p v-if="computedBaseConfig.class_calc !== 'none'" class="w-32 text-center">
                <span v-if="computedBaseConfig.class_calc === 'date_range'">Start Date</span>
                <span v-if="computedBaseConfig.class_calc === 'age'">Age Start</span>
              </p>
              <p v-if="computedBaseConfig.class_calc !== 'none'" class="w-32 text-center">
                <span v-if="computedBaseConfig.class_calc === 'date_range'">End Date</span>
                <span v-else-if="computedBaseConfig.class_calc === 'age'">Age End</span>
              </p>
            </div>
            <div class="w-10">Ch</div>
            <div class="w-8"></div>
            <!--<p>Break By</p>-->
          </div>
          <draggable v-model="config.classes" @start="drag=true" @end="drag=false" handle=".handle" >
            <div v-for="(c, i) in config.classes" :key="i" class="flex justify-between border-b py-2 space-x-1 items-center bg-white p-1">
              <div v-if="!divisionConfig" class="text-xl handle draggable">&#9776;</div>
              <input class="new-form-input mt-1 w-full" placeholder="Class Name" v-model="c.class_name" type="text">
              <input v-if="config.class_sub_divisions" class="new-form-input w-32 mt-1" placeholder="Sub Division" v-model="c.sub_division" type="text">
              <div v-if="computedBaseConfig.break_type === 'age' && !c.is_champion" class="relative flex items-center" >
                <div class="flex h-5 items-center">
                  <input v-model="c.by_weight" id="comments" aria-describedby="comments-description" name="comments" type="checkbox" class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                </div>
              </div>
              <template v-if="computedBaseConfig.class_calc !== 'none' && !c.is_champion">
                <input v-if="computedBaseConfig.class_calc === 'date_range'" class="new-form-input mt-1 w-32 text-sm" v-model="c.start_date" type="date">
                <input v-else-if="computedBaseConfig.class_calc === 'age'" class="new-form-input mt-1 w-32 text-sm" v-model="c.start_date" type="number">
                <input v-if="computedBaseConfig.class_calc === 'date_range'" class="new-form-input mt-1 w-32 text-sm" v-model="c.end_date" type="date">
                <input v-else-if="computedBaseConfig.class_calc === 'age'" class="new-form-input mt-1 w-32 text-sm" v-model="c.end_date" type="number">
              </template>
              <template v-if ="c.is_champion  && c.settings">
                <div class="w-36">
                  <div id="age-unit">
                    <select v-model="c.settings.champion_type" id="location" name="location" class="mt-1 block w-full rounded-md border border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm">
                      <option :value="null" selected disabled>
                        Please chooser the eligibility select type
                      </option>
                      <option value="STANDARD">Standard</option>
                      <option value="ANIMAL">Animal (Choose from any animal)</option>
                      <option value='EXHIBITOR'>Exhibitor</option>
                      <option value='WRITE'>Write-In</option>
                      <option value='FIELD'>Field Based (Animals)</option>
                    </select>
                  </div>
                </div>
                <div v-if="c.settings.champion_type === 'FIELD'">
                  <input class="new-form-input mt-1 w-full" placeholder="Filter Field" v-model="c.settings.filter_field" type="text">
                </div>
              </template>
              <div class="relative flex items-center">
                <div class="flex h-5 items-center">
                  <input v-model="c.is_champion" @change="isChampionChange(c)" id="comments" aria-describedby="comments-description" name="comments" type="checkbox" class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
                </div>
              </div>
              <button @click="config.classes.splice(i,1)" class='ml-2 btn btn-xs btn-red-outline'>x</button>
            </div>
          </draggable>
        </div>
      </div>
    </expandable>
    <expandable title="Advanced Settings" class="bg-gray-200">
      <div slot="title" class='flex items-center flex-1 p-3 space-x-1 w-full rounded'>
        <p class="font-bold">Advanced Settings</p>
      </div>
      <div slot="expandable" class="bg-white p-4">
        <div class="grid grid-rows- grid-flow-row grid-cols-1 sm:grid-cols-3 gap-4">
          <div class="relative flex items-start" v-if="level === 0">
            <div class="flex h-5 items-center">
              <input v-model="config.hide_from_division_list" id="comments" aria-describedby="comments-description" name="comments" type="checkbox" class="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
            </div>
            <div class="ml-3 text-sm">
              <label for="comments" class="font-medium text-gray-700">Don't Allow Division Select</label>
              <p id="comments-description" class="text-gray-600">Don't give option for choosing a division when adding entry.</p>
            </div>
          </div>
          <div class="relative flex items-start">
            <div class="flex h-4 items-center">
              <input id="comments" v-model="config.no_classes" aria-describedby="comments-description" name="comments" type="checkbox" class="h-3 w-3 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
            </div>
            <div class="ml-3 text-sm">
              <label for="comments" class="font-medium text-gray-700">No Classes</label>
              <p id="comments-description" class="text-gray-600">Ignore any classes assigned to this division</p>
            </div>
          </div>
          <div class="relative flex items-start" v-if="level > 0">
            <div class="flex h-4 items-center">
              <input id="comments" v-model="config.admin_only" aria-describedby="comments-description" name="comments" type="checkbox" class="h-3 w-3 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
            </div>
            <div class="ml-3 text-sm">
              <label for="comments" class="font-medium text-gray-700">Admin Only</label>
              <p id="comments-description" class="text-gray-600">Only show this option on the admin side.</p>
            </div>
          </div>
          <div class="relative flex items-start" v-if="level > 0">
            <div class="flex h-4 items-center">
              <input id="comments" v-model="config.use_only_division_name" aria-describedby="comments-description" name="comments" type="checkbox" class="h-3 w-3 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
            </div>
            <div class="ml-3 text-sm">
              <label for="comments" class="font-medium text-gray-700">Use Only Division in Name</label>
              <p id="comments-description" class="text-gray-600">Don't use the full built name for these division class names.</p>
            </div>
          </div>
          <template v-if="config.advanced_name_settings">
            <div>
              <label class="form-label">Champion Name/Label</label>
              <input class="new-form-input mt-1 w-full" v-model="config.champion_name" for="label/name" placeholder="Class List Label">
              <p class="mt-1 text-sm text-gray-700" id="question-description">The base "name" property will be used for classes and this will be used for Champion classes</p>
            </div>
            <div class="relative flex items-start" v-if="!config.classes && (hasAgeSettings)">
              <div class="flex h-4 items-center">
                <input id="comments" v-model="config.show_age_in_division_name" aria-describedby="comments-description" name="comments" type="checkbox" class="h-3 w-3 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500">
              </div>
              <div class="ml-3 text-sm">
                <label for="comments" class="font-medium text-gray-700">Show Ages In Division Name</label>
                <p class="mt-1 text-sm text-gray-700" id="question-description">The will include the division age ranges in the name of the division class only.</p>

              </div>
            </div>
          </template>
          <div>
            <label class="form-label">Pre-Set Division Breaks</label>
            <input class="new-form-input mt-1 w-full" v-model="config.pre_set_breaks" for="label/name" placeholder="Division Breaks">
            <p class="mt-1 text-sm text-gray-700" id="question-description">Used to indicated division breaks or subdivisions, list outnames comma seperated.</p>
          </div>
        </div>
      </div>
    </expandable>
    <expandable title="Advanced Confirguration" v-if="config.advanced_config" class="bg-gray-200">
      <div slot="title" class='flex items-center flex-1 p-3 space-x-1 w-full rounded'>
        <p class="font-bold">Advanced Configuration</p>
      </div>
      <div slot="expandable" class="bg-white p-4">
        <div class="flex justify-end">
          <button class="btn-sm mb-2 btn-green-outline" @click="config.advanced_config.push({})">Add Extra Champions</button>
        </div>
        <div v-for="(ac, i) in config.advanced_config" :key="i" class="border-t flex py-2 items-center">
          <div class="grid grid-rows- grid-flow-row grid-cols-1 sm:grid-cols-3 gap-4 flex-grow">
            <div>
              <label class="form-label">Champion Name</label>
              <input class="new-form-input mt-1 w-full" v-model="ac.name" for="label/name" placeholder="Champion Label">
            </div>
            <!--<div>
              <label class="form-label"># of Champions</label>
              <input type="number" class="new-form-input mt-1 w-full" v-model.number="ac.champions" for="label/name" placeholder="How many champions">
            </div>-->
            <div>
              <label class="form-label">Champion Select Type</label>
              <select v-model="ac.select_type" id="location" name="location" class="mt-1 block w-full rounded-md border border-gray-300 py-2 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm">
                <option :value="null" selected disabled>
                  Please chooser the eligibility select type
                </option>
                <option value="ANIMAL">Animal</option>
                <option value='EXHIBITOR'>Exhibitor</option>
                <option value='WRITE'>Write-In</option>
                <option value='FIELD'>Field Based (Animals)</option>
              </select>
              <p class="mt-1 text-sm text-gray-700" id="question-description">How do you want to refer to this champion?</p>
            </div>
            <div v-if="ac.select_type === 'FIELD'">
              <label class="form-label">Field Name</label>
              <input type="text" class="new-form-input mt-1 w-full" v-model="ac.filter_field" for="label/name" placeholder="">
            </div>
          </div>
          <button class="btn-xs btn-red ml-2" @click="config.advanced_config.splice(i,1)">X</button>
        </div>
      </div>
    </expandable>
  </div>
</template>

<script>
import Vue from 'vue'
import draggable from 'vuedraggable'
import CopyConfigTemplate from '@/components/admin/classListConfig/CopyConfigTemplate.vue'
import ImportConfigFromCSV from '@/components/admin/classListConfig/ImportConfigFromCSV.vue'
import Expandable from '@/components/Expandable'

export default {
  name: 'DivisionConfig',
  components: {
    draggable,
    CopyConfigTemplate,
    ImportConfigFromCSV,
    Expandable
  },
  props: {
    config: Object,
    baseConfig: {
      type: Object,
      default: () => {
        return {}
      }
    },
    level: Number,
    class_configuration_id: String
  },
  data () {
    return {
      newDivision: '',
      divisionConfig: null
    }
  },
  methods: {
    setConfigTemplate (configTemplate) {
      this.$emit('templateSet', configTemplate)
    },
    enableDivisions () {
      Vue.set(this.config, 'divisions', [])
      if (!this.config.division_champions) {
        this.config.division_champions = 2
      }
    },
    removeDivisions () {
      Vue.set(this.config, 'divisions', null)
    },
    enableClasses () {
      Vue.set(this.config, 'classes', [])
    },
    removeClasses () {
      Vue.set(this.config, 'classes', null)
    },
    addDivision () {
      // Check Basic Settings:
      if (!this.config.name || !this.config.break_type || this.config.champions === null) {
        alert('Please set all basic settings before adding divisions')
        return
      }
      if (this.newDivision.trim() === '') {
        alert('Please Add a valid division name')
        return
      }
      this.config.divisions.push({
        name: this.newDivision,
        champion_title: 'Champion',
        break_type: this.config.break_type,
        champions: this.config.division_champions,
        division_champions: this.config.division_champions
      })
      this.newDivision = ''
    },
    editDivisionClicked (d) {
      this.divisionConfig = d
      Vue.set(d, 'edit', true)
      Vue.set(d, 'active', true)
      // this.$refs.division_modal.show()
    },
    doneEditingDivision (d) {
      this.divisionConfig = null
      Vue.set(d, 'edit', false)
      Vue.set(d, 'active', false)
      // this.$refs.division_modal.hide()
    },
    updateDivisionChampions () {
      this.config.divisions.forEach(d => {
        d.champions = this.config.division_champions
      })
      this.$refs.DivisionDropdown.close()
    },
    updateBreakBy () {
      this.config.divisions.forEach(d => {
        d.break_type = this.config.break_type
      })
      this.$refs.DivisionDropdown.close()
    },
    addClass () {
      this.config.classes.push({
      })
    },
    enableAdvancedConfig () {
      Vue.set(this.config, 'advanced_config', [])
    },
    removeAdvancedConfig () {
      Vue.set(this.config, 'advanced_config', null)
    },
    isChampionChange (c) {
      if (c.is_champion) {
        if (!c.settings) {
          Vue.set(c, 'settings', {})
        }
        if (!c.settings.champion_type) {
          Vue.set(c.settings, 'champion_type', 'STANDARD')
        }
      } else {
        if (c.settings && c.settings.champion_type) {
          Vue.delete(c.settings, 'champion_type')
        }
      }
    }
  },
  computed: {
    hasAgeSettings () {
      if (this.baseConfig.name) {
        return this.baseConfig.class_calc === 'age' || this.baseConfig.class_calc === 'date_range'
      } else {
        return this.config.class_calc === 'age' || this.config.class_calc === 'date_range'
      }
    },
    computedBaseConfig () {
      return this.baseConfig.name ? this.baseConfig : this.config
    }
  }

}
</script>

<style>
.ghost {
  opacity: 0.5;
  background: #c8ebfb;
}
.draggable:hover {
  cursor: grab;
}
</style>
